/**
 * Ödül Öneri — yeni akış durum sözlüğü (greenfield).
 * Eski: manager→board→awaiting_final→award→implementation
 * Yeni: opex→manager→implementation→verify→award_pool→board
 */

export const RECORD_STATUS = {
  OPEX_PENDING: 'opex_pending',
  MANAGER_PENDING: 'manager_pending',
  CLOSED_NOT_SUGGESTION: 'closed_not_suggestion',
  CLOSED_REJECTED_MANAGER: 'closed_rejected_manager',
  IMPL_NOT_STARTED: 'impl_not_started',
  IMPL_IN_PROGRESS: 'impl_in_progress',
  IMPL_WAITING: 'impl_waiting',
  IMPL_DELAYED: 'impl_delayed',
  IMPL_CANCELLED: 'impl_cancelled',
  IMPL_RESULT_PENDING: 'impl_result_pending',
  IMPL_CORRECTION: 'impl_correction',
  IMPL_VERIFIED: 'impl_verified',
  AWARD_POOL: 'award_pool',
  CLOSED_AWARDED: 'closed_awarded',
  CLOSED_THANKS: 'closed_thanks',
  DEFERRED: 'deferred',
}

export const RECORD_STATUS_LABELS = {
  [RECORD_STATUS.OPEX_PENDING]: 'OPEX Ön İnceleme',
  [RECORD_STATUS.MANAGER_PENDING]: 'Bölüm Yöneticisi Kararı Bekleniyor',
  [RECORD_STATUS.CLOSED_NOT_SUGGESTION]: 'Kapandı (Öneri Değil)',
  [RECORD_STATUS.CLOSED_REJECTED_MANAGER]: 'Kapandı (Yönetici Red)',
  [RECORD_STATUS.IMPL_NOT_STARTED]: 'Uygulama Başlamadı',
  [RECORD_STATUS.IMPL_IN_PROGRESS]: 'Uygulama Devam Ediyor',
  [RECORD_STATUS.IMPL_WAITING]: 'Uygulama Beklemede',
  [RECORD_STATUS.IMPL_DELAYED]: 'Uygulama Gecikti',
  [RECORD_STATUS.IMPL_CANCELLED]: 'Uygulama İptal',
  [RECORD_STATUS.IMPL_RESULT_PENDING]: 'Sonuç Doğrulama',
  [RECORD_STATUS.IMPL_CORRECTION]: 'Düzeltme Bekleniyor',
  [RECORD_STATUS.IMPL_VERIFIED]: 'OPEX Doğrulandı',
  [RECORD_STATUS.AWARD_POOL]: 'Ödül Havuzu / Kurul Bekliyor',
  [RECORD_STATUS.CLOSED_AWARDED]: 'Kapandı (Ödül)',
  [RECORD_STATUS.CLOSED_THANKS]: 'Kapandı (Teşekkür)',
  [RECORD_STATUS.DEFERRED]: 'Kurul Erteledi',
}

export const getRecordStatusLabel = (status) =>
  RECORD_STATUS_LABELS[status] || status || '—'

export const isClosedStatus = (status) =>
  String(status || '').startsWith('closed_') || status === RECORD_STATUS.DEFERRED

export const isImplementationPhase = (status) =>
  String(status || '').startsWith('impl_')

/**
 * Lider token: süre ile bitmez; süreç kapandığında (OPEX doğrulama / iptal / kapanış) erişim kapanır.
 * Düzeltme beklerken ve ara ilerleme güncellemelerinde açık kalır.
 */
export const isLeaderAccessClosed = (record, implementation) => {
  if (implementation?.opexVerification?.result === 'verified') return true
  const status = record?.status || implementation?.record?.status
  if (!status) return false
  if (String(status).startsWith('closed_')) return true
  return (
    status === RECORD_STATUS.AWARD_POOL ||
    status === RECORD_STATUS.IMPL_CANCELLED ||
    status === RECORD_STATUS.DEFERRED
  )
}

/** Lider takip ekranı — eski tek-status dropdown (geriye dönük) */
export const LEADER_TRACKING_STATUSES = [
  RECORD_STATUS.IMPL_NOT_STARTED,
  RECORD_STATUS.IMPL_IN_PROGRESS,
  RECORD_STATUS.IMPL_WAITING,
  RECORD_STATUS.IMPL_DELAYED,
  RECORD_STATUS.IMPL_CORRECTION,
  RECORD_STATUS.IMPL_CANCELLED,
  RECORD_STATUS.IMPL_RESULT_PENDING,
]

/**
 * Uygulama Genel Durumu — lider/yönetici seçer (şablon appState).
 * Gecikti elle seçilmez; termin ile görüntüde otomatik.
 */
export const APP_STATE = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  WAITING: 'waiting',
  CANCELLED: 'cancelled',
}

export const APP_STATE_LABELS = {
  [APP_STATE.NOT_STARTED]: 'Uygulama Başlamadı',
  [APP_STATE.IN_PROGRESS]: 'Devam ediyor',
  [APP_STATE.COMPLETED]: 'Tamamlandı',
  [APP_STATE.WAITING]: 'Beklemede',
  [APP_STATE.CANCELLED]: 'İptal Edildi',
}

export const APP_STATE_OPTIONS = [
  APP_STATE.NOT_STARTED,
  APP_STATE.IN_PROGRESS,
  APP_STATE.COMPLETED,
  APP_STATE.WAITING,
  APP_STATE.CANCELLED,
]

export const getAppStateLabel = (appState) =>
  APP_STATE_LABELS[appState] || appState || '—'

const isoTodayTr = () =>
  new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())

/** Eski record.status → appState (migration) */
export const inferAppStateFromRecordStatus = (status) => {
  switch (status) {
    case RECORD_STATUS.IMPL_NOT_STARTED:
      return APP_STATE.NOT_STARTED
    case RECORD_STATUS.IMPL_WAITING:
      return APP_STATE.WAITING
    case RECORD_STATUS.IMPL_CANCELLED:
      return APP_STATE.CANCELLED
    case RECORD_STATUS.IMPL_RESULT_PENDING:
    case RECORD_STATUS.IMPL_CORRECTION:
    case RECORD_STATUS.IMPL_VERIFIED:
    case RECORD_STATUS.AWARD_POOL:
    case RECORD_STATUS.CLOSED_AWARDED:
    case RECORD_STATUS.CLOSED_THANKS:
      return APP_STATE.COMPLETED
    case RECORD_STATUS.IMPL_IN_PROGRESS:
    case RECORD_STATUS.IMPL_DELAYED:
    default:
      return APP_STATE.IN_PROGRESS
  }
}

export const resolveAppState = (impl, record) => {
  const stored = impl?.tracking?.appState
  if (stored && APP_STATE_LABELS[stored]) return stored
  return inferAppStateFromRecordStatus(record?.status || impl?.record?.status)
}

/** Termin geçmiş + henüz tamamlanmamış/iptal değilse görüntüde Gecikti */
export const isAppStateOverdue = (appState, dueDate, today = isoTodayTr()) => {
  if (!dueDate) return false
  if ([APP_STATE.COMPLETED, APP_STATE.CANCELLED].includes(appState)) return false
  return String(dueDate) < String(today)
}

export const displayAppStateLabel = (appState, dueDate) => {
  if (isAppStateOverdue(appState, dueDate)) return 'Gecikti'
  return getAppStateLabel(appState)
}

/**
 * appState + OPEX verification → kaydedilecek record.status
 * (düzeltme / ödül havuzu verification akışında ayrıca set edilir)
 */
export const deriveRecordStatusFromAppState = (appState, { verificationState } = {}) => {
  if (verificationState === 'closed' || verificationState === 'verified') {
    return RECORD_STATUS.AWARD_POOL
  }
  if (verificationState === 'returned') {
    return RECORD_STATUS.IMPL_CORRECTION
  }
  switch (appState) {
    case APP_STATE.NOT_STARTED:
      return RECORD_STATUS.IMPL_NOT_STARTED
    case APP_STATE.WAITING:
      return RECORD_STATUS.IMPL_WAITING
    case APP_STATE.CANCELLED:
      return RECORD_STATUS.IMPL_CANCELLED
    case APP_STATE.COMPLETED:
      return RECORD_STATUS.IMPL_RESULT_PENDING
    case APP_STATE.IN_PROGRESS:
    default:
      return RECORD_STATUS.IMPL_IN_PROGRESS
  }
}

/** OPEX havuzu / takip filtreleri */
export const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Tümü' },
  { value: 'opex_pending', label: 'OPEX Ön İnceleme' },
  { value: 'manager_pending', label: 'Yönetici Bekliyor' },
  { value: 'implementation', label: 'Uygulamada' },
  { value: 'award_pool', label: 'Ödül Havuzu' },
  { value: 'closed', label: 'Kapandı' },
]

export const matchesStatusFilter = (status, filter) => {
  if (!filter || filter === 'all') return true
  if (filter === 'implementation') return isImplementationPhase(status)
  if (filter === 'closed') return isClosedStatus(status)
  return status === filter
}

export const BOARD_DECISION = {
  AWARD: 'award',
  THANKS: 'thanks',
  DEFERRED: 'deferred',
}

export const BOARD_DECISION_LABELS = {
  [BOARD_DECISION.AWARD]: 'Ödül',
  [BOARD_DECISION.THANKS]: 'Teşekkür',
  [BOARD_DECISION.DEFERRED]: 'Erteleme',
}

export const formatIsoDate = (iso) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('tr-TR')
  } catch (_) {
    return iso
  }
}

export const formatIsoDateTime = (iso) => {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  } catch (_) {
    return iso
  }
}

const SLA_OPEN = new Set([RECORD_STATUS.OPEX_PENDING, RECORD_STATUS.MANAGER_PENDING])

const startOfDay = (date) => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

export const calcSlaDelay = (record, now = new Date()) => {
  if (!SLA_OPEN.has(record?.status) || !record?.dueAt) {
    return { overdueDays: 0, isOverdue: false, daysRemaining: null }
  }
  const due = startOfDay(new Date(record.dueAt))
  const today = startOfDay(now)
  if (Number.isNaN(due.getTime())) {
    return { overdueDays: 0, isOverdue: false, daysRemaining: null }
  }
  const daysRemaining = Math.round((due.getTime() - today.getTime()) / (24 * 60 * 60 * 1000))
  if (daysRemaining < 0) {
    return { overdueDays: Math.abs(daysRemaining), isOverdue: true, daysRemaining }
  }
  return { overdueDays: 0, isOverdue: false, daysRemaining }
}
